import { useState } from 'react';
import { MessageSquareQuote, Plus, Save, Trash2 } from 'lucide-react';
import type { AiPreferences } from '@mailpilot/shared';
import { Button, Field, Input, Segmented, Textarea } from '../ui';
import { FormFooter, SubSection, TipIconButton, Well } from './SettingsPrimitives';

export function emptyPreferences(mailboxId: string | null): AiPreferences {
  return {
    scope: mailboxId ? 'mailbox' : 'global',
    mailboxId,
    writingStyle: '',
    language: '',
    tone: '',
    greeting: '',
    signOff: '',
    responseLength: 'medium',
    businessDetails: '',
    faqs: [],
  };
}

function samePrefs(a: AiPreferences, b: AiPreferences): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function PrefsForm({ initial, onSave, saving, scopeLabel }: { initial: AiPreferences; onSave: (p: AiPreferences) => void; saving: boolean; scopeLabel: string }) {
  const [p, setP] = useState<AiPreferences>(initial);
  const set = <K extends keyof AiPreferences>(k: K, v: AiPreferences[K]) => setP((prev) => ({ ...prev, [k]: v }));
  const dirty = !samePrefs(p, initial);
  const setFaq = (i: number, patch: Partial<{ question: string; answer: string }>) => set('faqs', p.faqs.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  const submit = () => {
    onSave({
      ...p,
      faqs: p.faqs.map((f) => ({ question: f.question.trim(), answer: f.answer.trim() })).filter((f) => f.question || f.answer),
    });
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <SubSection title="Voice" hint="How the assistant should sound when it writes as you.">
        <Field label="Writing style" hint="Describe how you write, in your own words. e.g. “Warm but brief. Short paragraphs. No exclamation marks.”">
          {(id) => <Textarea id={id} rows={4} value={p.writingStyle} onChange={(e) => set('writingStyle', e.target.value)} />}
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Language" hint="Leave empty to reply in the sender's language.">
            {(id) => <Input id={id} value={p.language} placeholder="e.g. English (UK)" onChange={(e) => set('language', e.target.value)} />}
          </Field>
          <Field label="Tone">
            {(id) => <Input id={id} value={p.tone} placeholder="e.g. friendly, professional" onChange={(e) => set('tone', e.target.value)} />}
          </Field>
          <Field label="Greeting">
            {(id) => <Input id={id} value={p.greeting} placeholder="e.g. Hi {first name}," onChange={(e) => set('greeting', e.target.value)} />}
          </Field>
          <Field label="Sign-off" hint="Your signature is added separately.">
            {(id) => <Input id={id} value={p.signOff} placeholder="e.g. Best wishes," onChange={(e) => set('signOff', e.target.value)} />}
          </Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[0.8125rem] font-medium text-ink">Response length</p>
            <p className="text-xs text-muted">A guide for how much the assistant writes.</p>
          </div>
          <Segmented<AiPreferences['responseLength']>
            label="Response length"
            value={p.responseLength}
            onChange={(v) => set('responseLength', v)}
            options={[
              { value: 'short', label: 'Short' },
              { value: 'medium', label: 'Medium' },
              { value: 'detailed', label: 'Detailed' },
            ]}
          />
        </div>
      </SubSection>

      <div className="border-t border-line/70 pt-6">
        <SubSection title="Knowledge" hint="Facts the assistant may rely on. Never include passwords or secrets.">
          <Field label="Business details" hint="Opening hours, address, services, pricing policy, links.">
            {(id) => <Textarea id={id} rows={5} value={p.businessDetails} onChange={(e) => set('businessDetails', e.target.value)} />}
          </Field>
        </SubSection>
      </div>

      <div className="border-t border-line/70 pt-6">
        <SubSection
          title="Approved FAQs"
          hint="Answers you've approved. The assistant may reuse them verbatim when a question matches."
          actions={
            <Button size="sm" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => set('faqs', [...p.faqs, { question: '', answer: '' }])}>
              Add FAQ
            </Button>
          }
        >
          {p.faqs.length === 0 ? (
            <Well className="flex items-center gap-3 !py-3.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-surface text-muted">
                <MessageSquareQuote className="h-4 w-4" aria-hidden />
              </span>
              <p className="text-[0.8125rem] text-muted">No FAQs yet. Add one for the questions you answer most often.</p>
            </Well>
          ) : (
            <div className="space-y-3">
              {p.faqs.map((f, i) => (
                <Well key={i} className="space-y-4">
                  <div className="flex items-start gap-2">
                    <Field label={`Question ${i + 1}`} className="flex-1">
                      {(id) => <Input id={id} value={f.question} onChange={(e) => setFaq(i, { question: e.target.value })} placeholder="e.g. Do you offer refunds?" className="!bg-surface" />}
                    </Field>
                    <TipIconButton className="mt-6" size="sm" label={`Remove question ${i + 1}`} tipPlacement="bottom-end" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={() => set('faqs', p.faqs.filter((_, j) => j !== i))} />
                  </div>
                  <Field label="Answer">
                    {(id) => <Textarea id={id} rows={3} value={f.answer} onChange={(e) => setFaq(i, { answer: e.target.value })} className="!bg-surface" />}
                  </Field>
                </Well>
              ))}
            </div>
          )}
        </SubSection>
      </div>

      <FormFooter note={dirty ? 'Unsaved changes' : undefined}>
        {dirty && (
          <Button onClick={() => setP(initial)} disabled={saving}>
            Revert
          </Button>
        )}
        <Button type="submit" variant="primary" icon={<Save className="h-4 w-4" aria-hidden />} loading={saving} disabled={!dirty}>
          Save {scopeLabel} preferences
        </Button>
      </FormFooter>
    </form>
  );
}
